import {
  normalizeFilterTypeSelection,
  normalizeModelFilterType,
} from 'dashboard409a/utilities/modelFilterNormalization';
import { areDatesEqual } from 'utilities/dateUtils/dateUtils';
import { ModelView } from '../ModelViewsSelector/ModelViewsSelector.props';

/**
 * Fast comparison for flat arrays of primitives (strings/numbers).
 * REQUIREMENT: Arrays must be sorted beforehand if order doesn't matter.
 */
export const areArraysEqual = (arr1: (string | number)[], arr2: (string | number)[]): boolean => {
  // 1. Reference check
  if (arr1 === arr2) {
    return true;
  }
  
  // 2. Length check
  if (arr1.length !== arr2.length) {
    return false;
  }

  // 3. Scalar check
  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) {
      return false;
    }
  }

  return true;
};

export const hasViewChanged = (currentView: ModelView, comparisonState: ModelView) => {

  const sortedCurrentPreparerIds = [...currentView.preparerIds].sort((a, b) => a - b);
  const sortedCurrentReviewerIds = [...currentView.reviewerIds].sort((a, b) => a - b);
  const sortedPreparerIds = [...comparisonState.preparerIds].sort((a, b) => a - b);
  const sortedReviewerIds = [...comparisonState.reviewerIds].sort((a, b) => a - b);

  let currentSearchStatusArray: string[] = [];

  if (Array.isArray(currentView.searchStatus)) {
    currentSearchStatusArray = [...currentView.searchStatus].sort((a, b) => a.localeCompare(b));
  } else if (currentView.searchStatus) {
    currentSearchStatusArray = [currentView.searchStatus];
  }
  const sortedSearchStatusValues = [...comparisonState.searchStatus].sort((a, b) => a.localeCompare(b));
  // 1. Array checks
  const preparersChanged = !areArraysEqual(sortedCurrentPreparerIds, sortedPreparerIds);
  const reviewersChanged = !areArraysEqual(sortedCurrentReviewerIds, sortedReviewerIds);
  const statusChanged = !areArraysEqual(currentSearchStatusArray, sortedSearchStatusValues);
  
  // 2. Date / trailing checks
  const trailingChanged =
    Boolean(currentView.useTrailingDates) !== Boolean(comparisonState.useTrailingDates) ||
    (Boolean(currentView.useTrailingDates) &&
      Boolean(comparisonState.useTrailingDates) &&
      currentView.trailingMonths !== comparisonState.trailingMonths);

  const fromDateChanged =
    currentView.useTrailingDates && comparisonState.useTrailingDates
      ? false
      : !areDatesEqual(currentView.fromDate, comparisonState.fromDate);
  const toDateChanged =
    currentView.useTrailingDates && comparisonState.useTrailingDates
      ? false
      : !areDatesEqual(currentView.toDate, comparisonState.toDate);
  
  // 3. Filter type + checkbox selection (normalize legacy current/past shapes from localStorage)
  const typeChanged =
    normalizeModelFilterType(currentView.filterType) !== normalizeModelFilterType(comparisonState.filterType);

  const currentSelection = normalizeFilterTypeSelection(
    currentView.filterTypeSelection as unknown as Record<string, unknown>,
    currentView.filterType
  );
  const comparisonSelection = normalizeFilterTypeSelection(
    comparisonState.filterTypeSelection as unknown as Record<string, unknown>,
    comparisonState.filterType
  );
  const selectionChanged =
    currentSelection.assigned !== comparisonSelection.assigned ||
    currentSelection.unassigned !== comparisonSelection.unassigned ||
    currentSelection.all !== comparisonSelection.all;

  return preparersChanged || reviewersChanged || statusChanged || fromDateChanged || toDateChanged || trailingChanged || typeChanged || selectionChanged;
};